import type { BadgeTone } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';

/* One row of `/wms/fulfillment-provider/:id/invoices`. */
export type ProviderInvoice = {
  _id: string;
  invoiceNumber?: string;
  status?: string;
  createdAt?: string;
  totalAmount?: number;
  customerOrganizationId?: {
    company_name?: string;
    logo?: { url?: string };
  };
};

const STATUSES: Record<string, { label: string; tone: BadgeTone }> = {
  open: { label: 'Open', tone: 'info' },
  'pre-approval': { label: 'Pending Approval', tone: 'caution' },
  draft: { label: 'Draft', tone: 'neutral' },
};

/** A status the app has no wording for is shown as the API spells it. */
export function invoiceStatus(status?: string): {
  label: string;
  tone: BadgeTone;
} {
  if (!status) {
    return { label: '', tone: 'neutral' };
  }
  return (
    STATUSES[status] ?? {
      label: CommonUtility.toTitleCase(status),
      tone: 'neutral',
    }
  );
}

export function invoiceLink(invoiceId: string, providerId: string) {
  return `/app/scm/storage-provider/invoice/${invoiceId}/?providerId=${providerId}`;
}

/* An organization on either side of an invoice: the provider bills, the customer pays. */
export type InvoiceParty = {
  name?: string;
  company_name?: string;
  email?: string;
  address?: Parameters<typeof CommonUtility.addressText>[0];
  logo?: { url?: string };
};

export type InvoiceLineItem = {
  _id?: string;
  category?: string;
  serviceName?: string;
  unit?: string;
  description?: string;
  pricePerUnit?: number;
  quantity?: number;
  amount?: number;
};

export type InvoiceAdjustment = {
  _id?: string;
  label?: string;
  type?: string;
  amount?: number;
};

/* `/wms/fulfillment-provider/:id/invoices/:invoiceId`. */
export type ProviderInvoiceDetail = {
  _id: string;
  invoiceNumber?: string;
  status?: string;
  dueDate?: string;
  invoiceDate?: string;
  memo?: string;
  subtotal?: number;
  totalAmount?: number;
  lineItems?: InvoiceLineItem[];
  adjustments?: InvoiceAdjustment[];
  customerOrganizationId?: InvoiceParty;
  issuedByOrganizationId?: InvoiceParty;
};

/* One entry of the invoice's history. No `performedBy` means the system did it. */
export type InvoiceLog = {
  _id: string;
  message?: string;
  createdAt?: string;
  performedBy?: { name?: string };
};

/** The customer goes by `company_name`, the provider's own organization by `name`. */
export function partyName(party?: InvoiceParty): string {
  return party?.company_name || party?.name || '';
}

export function invoicePdfName(
  invoice: Pick<ProviderInvoiceDetail, 'invoiceNumber'>,
) {
  return `invoice-${invoice.invoiceNumber ?? ''}.pdf`;
}
